import { startCase } from 'lodash';
import constants, { FILTER_DATA_TYPES } from '../../const/Const';

/**
 * Returns the filters config for the History page in the new structure.
 * This is functionally equivalent to the filtersConfig useMemo in History.js
 * but matches the structure of TTRReportFilters.js (only common keys).
 */

export const INITIAL_HISTORY_FILTERS = {
  screeningType: 'OngoingScreening',
  module: 'name_screen',
  search_format: '',
};

export function getHistoryFilters({
  user,
  analystList,
  teamList,
  managers,
  historyFilter,
  analystOptions,
  SEARCH_TYPES,
  sortOptions,
  formatUserDisplayName,
  riskRatings = [],
  screen,
  list,
}) {
  const filters = [
    {
      key: 'module',
      label: 'Module',
      datatype: FILTER_DATA_TYPES.SINGLESELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Name Screening', value: 'name_screen' },
        { label: 'Transaction Screening', value: 'transaction_screen' },
      ],
      isDefault: true,
      isRequired: true,
    },
    {
      key: 'screeningType',
      label: 'Screening Type',
      datatype: FILTER_DATA_TYPES.SINGLESELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Initial Screening', value: 'InitialScreening' },
        { label: 'Ongoing Monitoring', value: 'OngoingScreening' },
      ],
      isDefault: true,
      isRequired: true,
    },
    {
      key: 'alerted',
      label: 'Alert/Non-Alert',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Alert', value: 'true' },
        { label: 'Non-Alert', value: 'false' },
      ],
      isDefault: true,
    },
    {
      key: 'alertListType',
      label: 'Alert List Type',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: list ?? [],
      optionFallback: 'Select option Alert to select alert list type',
      isDefault: true,
    },
  ];
  if (historyFilter?.module !== 'transaction_screen') {
    filters.push({
      key: 'searchType',
      label: 'Search Type',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: SEARCH_TYPES.i.label, value: SEARCH_TYPES.i.key },
        { label: SEARCH_TYPES.e.label, value: SEARCH_TYPES.e.key },
        { label: SEARCH_TYPES.v.label, value: SEARCH_TYPES.v.key },
        { label: SEARCH_TYPES.a.label, value: SEARCH_TYPES.a.key },
        { label: SEARCH_TYPES.b.label, value: SEARCH_TYPES.b.key },
      ],
      isDefault: true,
    });
  }
  if (historyFilter?.module === 'transaction_screen') {
    filters.push({
      key: 'search_format',
      label: 'Transaction Format',
      datatype: FILTER_DATA_TYPES.SINGLESELECT,
      hasStaticOptions: true,
      options: constants.SEARCH_FORMAT_OPTIONS,
      isDefault: true,
      defaultValue: 'TS-ISO',
      isRequired: true,
    });
  }

  // Conditionally add Risk Rating filter
  if (
    !(historyFilter?.searchType === 'v' || historyFilter?.searchType === 'a') &&
    historyFilter?.module !== 'transaction_screen'
  ) {
    filters.push({
      key: 'riskType',
      label: 'Risk Rating',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: riskRatings.map((r) => ({ label: startCase(r), value: r })),
      isDefault: false,
    });
  }

  filters.push({
    key: 'finalVerdict',
    label: 'Final Verdict',
    datatype: FILTER_DATA_TYPES.MULTISELECT,
    hasStaticOptions: true,
    options: [
      { label: 'Accepted', value: 'Accepted' },
      { label: 'Rejected', value: 'Rejected' },
    ],
    isDefault: false,
  });
  filters.push({
    key: 'true_match',
    label: 'True Match',
    datatype: FILTER_DATA_TYPES.MULTISELECT,
    hasStaticOptions: true,
    options: [
      { label: 'Sanction True Match', value: 'sanction_true_match' },
      { label: 'PEP True Match', value: 'pep_true_match' },
      { label: 'AMS True Match', value: 'ams_true_match' },
    ],
    isDefault: true,
  });

  // Conditionally add Select Manager filter for admin
  if (['admin'].includes(user?.role)) {
    filters.push({
      key: 'manager_id',
      label: 'Select Manager',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        analystList?.length > 0
          ? sortOptions(analystList)
              .filter((opt) => opt?.analyst_level === -1)
              .map((opt) => ({
                label: formatUserDisplayName(opt),
                value: opt.id,
              }))
          : [
              {
                label: 'No manager found',
                value: null,
                disabled: true,
              },
            ],
      isDefault: false,
    });
  }

  filters.push({
    key: 'team',
    label: 'Select Team',
    datatype: FILTER_DATA_TYPES.MULTISELECT,
    hasStaticOptions: true,
    options:
      teamList?.length > 0
        ? [
            { label: 'Manual', value: 'manual' },
            ...sortOptions(teamList)
              .filter((opt) => {
                if (user?.role === 'maker' || user?.role === 'checker') {
                  return opt?.analysts?.some(
                    (analyst) => analyst.username === user?.username
                  );
                }

                if (!managers) return true;
                const managerId = parseInt(managers, 10);
                const selectedManager = analystList?.find(
                  (analyst) => analyst?.id === managerId
                );
                const managerUsername = selectedManager?.username;
                if (!managerUsername) return true;
                // Check if manager created the team or is a member
                const isCreator =
                  opt?.created_by?.username === managerUsername ||
                  opt?.created_by === managerUsername;
                const isMember = opt?.analysts?.some(
                  (analyst) => analyst?.username === managerUsername
                );

                return isCreator || isMember;
              })
              .map((option) => ({
                label: `${option.name} (${option.id})`,
                value: option.id,
              })),
          ]
        : [{ label: 'No Team Available', value: null, disabled: true }],
    isDefault: false,
  });

  filters.push({
    key: 'analyst',
    label: 'Select Analyst (Reviewed by)',
    datatype: FILTER_DATA_TYPES.MULTISELECT,
    hasStaticOptions: true,
    options: (() => {
      const selectedTeams = historyFilter?.team || [];
      if (selectedTeams.length === 1 && selectedTeams[0] === 'manual') {
        return [{ label: 'No Option Available', value: null, disabled: true }];
      }
      return analystOptions?.length > 0
        ? sortOptions(analystOptions)?.map((opt) => ({
            label: formatUserDisplayName(opt),
            value: opt.username,
          })) || []
        : [{ label: 'No analysts available', value: null, disabled: true }];
    })(),
    isDefault: false,
  });

  filters.push({
    key: 'initiatedDateRange',
    label: 'Initiate Date Range',
    datatype: FILTER_DATA_TYPES.DATE,
    hasStaticOptions: true,
    options: [],
    isDefault: false,
    disableFuture: true,
    limitThisMonthToToday: true,
  });

  filters.push({
    key: 'verdictDateRange',
    label: 'Verdict Date Range',
    datatype: FILTER_DATA_TYPES.DATE,
    hasStaticOptions: true,
    options: [],
    isDefault: false,
    disableFuture: true,
    limitThisMonthToToday: true,
  });

  if (historyFilter?.module !== 'transaction_screen') {
    filters.push({
      key: 'search_source',
      label: 'Search Source',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Reassigned', value: 'reassigned' },
        { label: 'Assigned', value: 'assigned' },
        { label: 'Referred', value: 'referred' },
        ...(screen === constants.HISTORY_SCREEN_TYPES.o.label
          ? [{ label: 'Rekyc', value: 'rekyc' }]
          : []),
        ...(screen === constants.HISTORY_SCREEN_TYPES.o.label
          ? [{ label: 'IS to OM', value: 'is_to_om' }]
          : []),
        { label: 'Transferred', value: 'transferred' },
        ...(screen === constants.HISTORY_SCREEN_TYPES.o.label
          ? [{ label: 'Daily Monitoring', value: 'daily_monitoring' }]
          : []),
      ],
      isDefault: false,
    });
  }

  return filters;
}
